import Vue from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { Blockchain, BlockchainNames } from '@/constants/blockchain'
import { RoomTypes } from '@/constants/chat/RoomTypes'
import { DISPUTE_GIG_TYPE } from '@/constants/components/CreateDispute'
import { CURRENCY_FIELD_NAME } from '@/constants/currency'
import { GigJobStages } from '@/constants/gig/gigJobStages'
import { GigStatuses } from '@/constants/gig/gigStatuses'
import { SERVICE_CHAT, SERVICE_DETAILS } from '@/constants/routes'
import { DATE_TIME_FORMAT_BY_MERIDIEM } from '@/constants/utils/date'
import responseMixin from '@/mixins/responseMixin'
import snackMixin from '@/mixins/snackMixin'
import ChatRoom from '@/models-ts/chat/ChatRoom'
import Currency from '@/models-ts/Currency'
import GigOfferListItem from '@/models-ts/gigs/GigOfferListItem'
import GigJobStatusBadge from '@/partials/StatusBadges/GigJobStatusBadge/GigJobStatusBadge.vue'
import UserInfo from '@/partials/UserInfo/UserInfo.vue'
import { googleAnalyticsV2 } from '@/servicies-ts/analytics'
import { convertToUsd, getCurrency } from '@/utils-ts/currencies'
import { getTxLink } from '@/utils-ts/transaction'
import { addDays, addSeconds, convertToLocal, formatDate, isBefore } from '@/utils/date'
import { formatCurrency } from '@/utils/moneyFormat'

const EXTRA_DAYS = 2

export default Vue.extend<any, any, any, any>({
  mixins: [responseMixin, snackMixin],
  components: {
    GigJobStatusBadge,
    UserInfo,
  },
  props: {
    gig: GigOfferListItem,
    isFreelancer: Boolean,
  },
  data() {
    return {
      SERVICE_DETAILS,
    }
  },
  computed: {
    ...mapState({
      payToFreelancerLoading: (state: any) => state.gig.payToFreelancerLoading,
      returnFundsToCustomerLoading: (state: any) => state.gig.returnFundsToCustomerLoading,
      rates: (state: any) => state.rates.rates.value || [],
      isBalancesLoaded: (state: any) => state.balances.balances.isLoaded,
    }),
    ...mapGetters({
      getUnreadCount: 'chatNew/getUnreadCount',
    }),
    hasGig() {
      return !this.gig.isRemoved && (this.isFreelancer || this.gig.gigStatus === GigStatuses.PUBLISHED)
    },
    user() {
      return this.isFreelancer ? this.gig.customerProfile : this.gig.freelancerProfile
    },
    hasChat() {
      return this.gig.applicationId
    },
    hasPayBtn() {
      return !this.isFreelancer && this.gig.job.stage === GigJobStages.IN_PROGRESS
    },
    hasRefundBtn() {
      return !this.isFreelancer && this.gig.job.stage === GigJobStages.IN_PROGRESS && this.refundHasCome
    },
    hasDisputeBtn() {
      return this.isFreelancer && this.gig.job.stage === GigJobStages.IN_PROGRESS && this.deadlineHasCome
    },
    hasPayContractLoading() {
      return !!this.payToFreelancerLoading[this.gig.sc_id]
    },
    hasRefundContractLoading() {
      return !!this.returnFundsToCustomerLoading[this.gig.sc_id]
    },
    chatRoomId() {
      return ChatRoom.generateRoomId({
        type: RoomTypes.GIG,
        applicationId: this.gig.applicationId,
        freelancerId: this.gig.freelancerId,
        customerId: this.gig.customerId,
      })
    },
    chatLink() {
      return {
        name: SERVICE_CHAT,
        params: { id: this.gig.gig_id, slug: this.gig.slug },
        query: { room: this.chatRoomId },
      }
    },
    chatPayLink() {
      return {
        ...this.chatLink,
        query: {
          ...this.chatLink.query,
          action: 'pay',
        },
      }
    },
    chatRefundLink() {
      return {
        ...this.chatLink,
        query: {
          ...this.chatLink.query,
          action: 'refund',
        },
      }
    },
    unreadMsgs() {
      return this.getUnreadCount(this.chatRoomId)
    },
    deadline() {
      return formatDate(addSeconds(convertToLocal(this.gig.job.created_at), this.gig.deadline), DATE_TIME_FORMAT_BY_MERIDIEM)
    },
    refundDate() {
      return formatDate(addDays(this.deadline, EXTRA_DAYS), DATE_TIME_FORMAT_BY_MERIDIEM)
    },
    deadlineHasCome() {
      return isBefore(this.deadline, Date.now())
    },
    refundHasCome() {
      return isBefore(this.refundDate, Date.now())
    },
    transactionStartLink() {
      return getTxLink({ blockchain: this.gig.blockchain, tx: this.gig.job.txid_created })
    },
    transactionEndLink() {
      return getTxLink({ blockchain: this.gig.blockchain, tx: this.gig.job.txid_completed })
    },
    isCompleted() {
      return [GigJobStages.PAYED, GigJobStages.RETURNED, GigJobStages.DISPUTED].includes(this.gig.job?.stage)
    },
    blockchainName() {
      return BlockchainNames[this.gig.blockchain as Blockchain]
    },
    currency() {
      return getCurrency({ blockchain: this.gig.blockchain, value: this.gig.currency, field: CURRENCY_FIELD_NAME })
    },
    escrowBalance() {
      return formatCurrency(this.gig.job.escrow_balance, { currency: this.currency })
    },
    escrowBalanceInUsd() {
      return this.rates.length
        ? convertToUsd({ value: this.gig.job.escrow_balance, currency: this.currency, rates: this.rates, format: true })
        : null
    },
  },
  methods: {
    ...mapActions({
      openModal: 'ui/openModal',
    }),
    refreshList() {
      this.$emit('refreshList')
    },
    onChatClick() {
      this.$router.push(this.chatLink)
    },
    onCreateDisputeClick() {
      if (this.deadlineHasCome) {
        this.openModal({
          component: 'lx-lazy-modal',
          props: {
            factory: import(/* webpackChunkName: "job-modals" */ '@/modals/CreateDispute/CreateDispute.vue'),
            title: 'Open a dispute',
            props: {
              disputeData: {
                type: DISPUTE_GIG_TYPE,
                id: this.gig.job.id,
                name: this.gig.name,
                blockchain: this.gig.blockchain,
              },
              onSuccess: () => {
                this.refreshList()
              },
            },
          },
        })
      } else {
        this.openSnackbar({
          type: this.SnackTypes.FAILURE,
          text: `You can only initiate a dispute once the deadline has been reached.`,
        })
      }
    },
    onPayToTalentClick() {
      this.$router.push(this.chatPayLink)
    },
    onRefundClick() {
      this.$router.push(this.chatRefundLink)
    },
    sendSuccessAnalytics(tx: string, walletName: string, currency: Currency, blockchain: Blockchain) {
      googleAnalyticsV2.send({
        event: 'offer-gig-payment',
        'event-content': tx,
        offer_id: this.gig.id,
        gig_id: this.gig.gig_id,
        block_type: BlockchainNames[blockchain],
        currency: currency.name,
        sum: this.gig.job.escrow_balance ? convertToUsd({ value: this.gig.job.escrow_balance, currency, rates: this.rates }) : '0',
        wallet_integration_type: walletName,
      })
    },
  },
})
